import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { SCREEN_SIZE } from 'types';
import { ArrowRight16 } from '@carbon/icons-react';
import {
  Button,
  TextInput,
  Form,
  Dropdown,
  InlineLoading
} from 'carbon-components-react';
import { selectScreenSize } from 'redux/session/selectors';
import { useFormik } from 'formik';
import { OnboardingProfileSchema } from 'utilities/validation/onboardingProfile.validation';
import { FormError } from 'components';
import Utils from 'utilities/helper.util';
import { ProfileFormValues } from '../localStore/types';

const industryItem = [
  { label: 'Construction', value: 'const' },
  { label: 'Government', value: 'gov' },
  { label: 'Locator', value: 'loc' },
  { label: 'Utilities', value: 'util' },
  { label: 'Other', value: 'other' }
];

const companySizeItem = [
  { label: '1', value: '1' },
  { label: '2 - 10', value: '2-10' },
  { label: '11 - 50', value: '11-50' },
  { label: '51 - 100', value: '51-100' },
  { label: '101+', value: '101' }
];

const estimatedEnquiriesItem = [
  { label: '1 - 10 / month', value: '1-10' },
  { label: '11 - 30 / month', value: '11-30' },
  { label: '31 - 60 / month', value: '31-60' },
  { label: '61 - 100 / month', value: '61-100' },
  { label: '101+ / month', value: '101' }
];

const smallScreens = [SCREEN_SIZE.MD, SCREEN_SIZE.SM];

const Profile: React.FC<Props> = ({
  onSubmit,
  profileValues,
  isTrialUser,
  isSavingUserProfile
}) => {
  const screenSize = useSelector(selectScreenSize);
  const [nextButtonDisabled, setNextButtonDisabled] = useState<boolean>(true);

  const profileFormInitialValues = {
    companyName: profileValues.companyName,
    contactPhoneNumber: profileValues.contactPhoneNumber,
    jobTitle: profileValues.jobTitle,
    industry: profileValues.industry,
    companySize: profileValues.companySize,
    estimatedEnquiries: profileValues.estimatedEnquiries,
    isTrialUser: isTrialUser
  };

  const {
    touched,
    errors,
    values,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    handleBlur
  } = useFormik<ProfileFormValues>({
    enableReinitialize: true,
    initialValues: profileFormInitialValues,
    validationSchema: OnboardingProfileSchema,
    onSubmit: (values: ProfileFormValues) => {
      onSubmit(values);
    }
  });

  const getOptionItem = (type: string, value: string) => {
    if (type === 'industry') {
      return industryItem.find((o) => value === o.value);
    } else if (type === 'companySize') {
      return companySizeItem.find((o) => value === o.value);
    } else if (type === 'estimatedEnquiries') {
      return estimatedEnquiriesItem.find((o) => value === o.value);
    }
  };

  const handleDropdownChange = (key: any, { selectedItem }: any) => {
    if (key && selectedItem?.value) {
      setFieldValue(key, selectedItem?.value);
      setFieldTouched(key, true, true);
    }
  };

  const handleTextInputChange = (key: string, val: string) => {
    if (key === 'contactPhoneNumber') {
      if (val.match(/^[0-9- ]+$/) || val === '') {
        setFieldTouched(key, true, true);
        setFieldValue(key, val);
      }
    } else {
      setFieldTouched(key, true, true);
      setFieldValue(key, val);
    }
  };

  useEffect(() => {
    if (
      Utils.isObjectEmpty(errors) &&
      values.companyName &&
      values.contactPhoneNumber
    ) {
      setNextButtonDisabled(false);
    } else {
      setNextButtonDisabled(true);
    }
  }, [errors, touched, values]);

  return (
    <Form id="profileForm" onSubmit={handleSubmit}>
      <div className="form-fields-wrapper">
        <div
          className={`textinput-fields-wrapper padding ${
            !isTrialUser ? 'trial-users' : ''
          }`}
        >
          <div id="companyNameSection" className="form-fields">
            <TextInput
              id="companyName"
              name="companyName"
              className="custom-fields-styling"
              labelText="Company name"
              placeholder="required"
              onChange={(val) =>
                handleTextInputChange('companyName', val.target.value)
              }
              onBlur={handleBlur}
              value={values.companyName}
              size={'lg'}
              maxLength={150}
              readOnly={!isTrialUser}
            />
            {errors.companyName && touched.companyName && (
              <FormError>{errors.companyName}</FormError>
            )}
          </div>
          <div id="contactPhoneNumberSection" className="form-fields">
            <TextInput
              id="contactPhoneNumber"
              name="contactPhoneNumber"
              className="custom-fields-styling"
              labelText="Contact phone number"
              placeholder="required"
              onChange={(val) =>
                handleTextInputChange('contactPhoneNumber', val.target.value)
              }
              onBlur={handleBlur}
              value={values.contactPhoneNumber}
              size={'lg'}
              maxLength={16}
            />
            {errors.contactPhoneNumber && touched.contactPhoneNumber && (
              <FormError>{errors.contactPhoneNumber}</FormError>
            )}
          </div>
          <div id="jobTitleSection" className="form-fields">
            <TextInput
              id="jobTitle"
              name="jobTitle"
              className="custom-fields-styling"
              labelText="Job title"
              onChange={(val) =>
                handleTextInputChange('jobTitle', val.target.value)
              }
              onBlur={handleBlur}
              value={values.jobTitle}
              size={'lg'}
              maxLength={150}
            />
            {errors.jobTitle && touched.jobTitle && (
              <FormError>{errors.jobTitle}</FormError>
            )}
          </div>
        </div>
        {screenSize && smallScreens.includes(screenSize) && isTrialUser && (
          <div className="divider"></div>
        )}
        {isTrialUser && (
          <div className="dropdown-fields-wrapper padding">
            <div id="industrySection" className="form-fields">
              <Dropdown
                id="industry"
                className="custom-fields-styling"
                titleText="Industry"
                label="Choose an option"
                items={industryItem}
                size={'lg'}
                initialSelectedItem={getOptionItem('industry', values.industry)}
                onBlur={handleBlur}
                onChange={(val) => handleDropdownChange('industry', val)}
              />
              {errors.industry && touched.industry && (
                <FormError>{errors.industry}</FormError>
              )}
            </div>
            <div id="companySizeSection" className="form-fields">
              <Dropdown
                id="companySize"
                className="custom-fields-styling"
                titleText="Company size"
                label="Choose an option"
                items={companySizeItem}
                size={'lg'}
                initialSelectedItem={getOptionItem(
                  'companySize',
                  values.companySize
                )}
                onBlur={handleBlur}
                onChange={(val) => handleDropdownChange('companySize', val)}
              />
              {errors.companySize && touched.companySize && (
                <FormError>{errors.companySize}</FormError>
              )}
            </div>
            <div id="estimatedEnquiriesSection" className="form-fields">
              <Dropdown
                id="estimatedEnquiries"
                className="custom-fields-styling"
                titleText="Estimated enquiries"
                label="Choose an option"
                items={estimatedEnquiriesItem}
                size={'lg'}
                initialSelectedItem={getOptionItem(
                  'estimatedEnquiries',
                  values.estimatedEnquiries
                )}
                onBlur={handleBlur}
                onChange={(val) =>
                  handleDropdownChange('estimatedEnquiries', val)
                }
              />
              {errors.estimatedEnquiries && touched.estimatedEnquiries && (
                <FormError>{errors.estimatedEnquiries}</FormError>
              )}
            </div>
          </div>
        )}
      </div>
      <div id="formButtons" className="form-buttons-wrapper padding">
        <Button
          kind="secondary"
          type="submit"
          size={'md'}
          renderIcon={isSavingUserProfile ? '' : ArrowRight16}
          disabled={nextButtonDisabled || isSavingUserProfile}
        >
          {!isSavingUserProfile ? (
            'Next'
          ) : (
            <InlineLoading
              description={!isSavingUserProfile ? 'Next' : 'Submitting...'}
              status={!isSavingUserProfile ? 'finished' : 'active'}
              aria-live={'assertive'}
            />
          )}
        </Button>
      </div>
    </Form>
  );
};

type Props = {
  onSubmit: Function;
  profileValues: ProfileFormValues;
  isTrialUser: boolean;
  isSavingUserProfile?: boolean;
};

export default React.memo(Profile);
